import { Button, Card, FormLayout, Heading, Paragraph, Text } from '@expressable/syntax-ds';
import { SealCheck, SealQuestion, SealWarning } from '@phosphor-icons/react';
import { Elements } from '@stripe/react-stripe-js';
import classNames from 'classnames';
import { AppointmentPaymentInput } from 'components/AppointmentPayment/AppointmentPaymentInput';
import { getStripeFromAccountID } from 'domains/billing/helpers';
import { useStripeAccountId } from 'domains/billing/hooks';

import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { InsuranceDefinitionV2 } from 'domains/signupDirect/misc/insuranceOptions';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { EvaluationFormType, paymentMethodIsInsurance } from 'domains/signupDirect/schemas/evaluationSchema';
import { isUndefined } from 'lodash';
import { FC, useMemo } from 'react';
import { useController, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { formatCurrencyDollars } from 'utils/currency';
import { useTrack } from 'utils/hooks/useMixPanel';
import { getEvaluationPrice } from '../misc/evalPrice';
import { DepositDueCardBody } from '../Components/DespositDueCardBody';
import { DEFAULT_IN_NETWORK_ESTIMATES, OUT_OF_NETWORK_ESTIMATES } from './constants';
import { useFlags } from 'launchdarkly-react-client-sdk';

export enum EligibilityCheckResult {
  IN_NETWORK = 'inn',
  INACTIVE = 'inactive',
  ERROR = 'error',
  OUT_OF_NETWORK = 'onn',
  INCORRECT_MEMBER_ID = 'incorrectmemberid',
  INCORRECT_NAME = 'incorrectname',
  INCORRECT_DOB = 'incorrectdob',
  INVALID_PARTICIPANT_ID = 'invalidparticipantid',
}

type Props = {
  result?: EligibilityCheckResult;
  isSkippedEligibility?: boolean;
  onSubmitEligibilityCheck?: () => void;
  showUpdateInfo?: boolean;
  setShowUpdateInfo?: (show: boolean) => void;
  onSubmitInsuranceCard?: () => void;
};

type BaseCardProps = {
  variant: 'accepted' | 'rejected' | 'error';
  title: string;
  body?: React.ReactNode;
  footer?: React.ReactNode;
  isSkippedEligibility?: boolean;
};

const BaseCard: FC<BaseCardProps> = ({ title, variant, body, children, footer, isSkippedEligibility }) => {
  return (
    <Card noDefaultPaddings>
      <div className="flex flex-col divide-y-2">
        {!isSkippedEligibility && (
          <Heading level="h6" className="gap-e16 flex items-center p-e32" noDefaultMargins>
            <div
              className={classNames('p-e10 rounded-full ', {
                'bg-green-100 text-green-600': variant === 'accepted',
                'bg-orchid-50 text-purple-500': variant === 'rejected',
                'bg-orange-50 text-red-900': variant === 'error',
              })}
            >
              {variant === 'accepted' && <SealCheck weight="fill" size={20} />}
              {variant === 'rejected' && <SealWarning weight="fill" size={20} />}
              {variant === 'error' && <SealQuestion weight="fill" size={20} />}
            </div>
            {title}
          </Heading>
        )}
        {body && <div className="p-e32 flex flex-col gap-e32">{body}</div>}
      </div>

      <FormLayout.CTA className="p-e32 pt-0 mobile-d:flex-col empty:hidden">{children}</FormLayout.CTA>
      {footer && <div className="p-e32 flex flex-col gap-e32">{footer}</div>}
    </Card>
  );
};

const Estimates = ({ insuranceData }: { insuranceData: InsuranceDefinitionV2 }) => {
  const { t } = useTranslation();

  const insuranceHasEstimates = checkInsuranceHasEstimates(insuranceData);
  const { filters } = useSignupDirectCtx();
  const { data } = useStripeAccountId(filters?.stateCode ?? '');
  const flags = useFlags();
  const { isSubmitting } = useFormState();
  const { field } = useController({
    name: 'billingInfo.stripeMethodId',
  });

  const stripeElement = useMemo(() => {
    if (!data) return null;
    return getStripeFromAccountID(data.accountId);
  }, [data]);

  const insuranceEstimates = useMemo(() => {
    if (insuranceHasEstimates) {
      return insuranceData;
    }

    if (!insuranceHasEstimates && insuranceData.networkStatus === 'Out-of-network') {
      return OUT_OF_NETWORK_ESTIMATES;
    }

    return DEFAULT_IN_NETWORK_ESTIMATES;
  }, [insuranceHasEstimates, insuranceData]);

  const shouldChargeCommercialDeposits = useMemo(() => {
    if (insuranceData.insuranceType === 'Commercial' && insuranceData.networkStatus === 'In-network') {
      return flags.chargeCommercialDeposits ?? false;
    }
    return false;
  }, [flags, insuranceData]);

  const evaluationPrice = getEvaluationPrice({
    isPrivatePay: false,
    insurance: insuranceData,
    isOutOfNetwork: insuranceData.networkStatus === 'Out-of-network',
    chargeCommercialDepositsFeatureFlag: shouldChargeCommercialDeposits,
  });

  return (
    <>
      {insuranceData.insuranceType === 'Commercial' && insuranceData.networkStatus === 'In-network' && (
        <>
          <div className="flex flex-col gap-y-e16 gap-x-e24 md:flex-row md:justify-between">
            <DepositDueCardBody
              price={formatCurrencyDollars(evaluationPrice)}
              insuranceData={insuranceEstimates as InsuranceDefinitionV2}
            />
          </div>
          <>
            {stripeElement && insuranceData.networkStatus === 'In-network' && (
              <Elements stripe={stripeElement}>
                <AppointmentPaymentInput
                  setPaymentMethod={method => field.onChange(method?.id)}
                  rhfName="billingInfo.stripeMethodId"
                  label={t('directSignup.eval.payment.labels.creditCard')}
                  disabled={isSubmitting}
                />
              </Elements>
            )}
          </>

          <Text variant="hint" className="mt-8 mb-8">
            {t('directSignup.eligibility.estimates.creditCardDisclaimer')}
          </Text>
        </>
      )}

      {insuranceData.insuranceType === 'Medicare' && (
        <>
          <div className="flex flex-col gap-y-e16 gap-x-e24 md:flex-row md:justify-between">
            <div className="flex flex-col">
              <Text variant="label" className="mb-e8">
                Deposit Due Today
              </Text>
              <Text variant="display" className="mb-e16">
                {formatCurrencyDollars(evaluationPrice)}
              </Text>
            </div>
          </div>
          <>
            {stripeElement && (
              <Elements stripe={stripeElement}>
                <AppointmentPaymentInput
                  setPaymentMethod={method => field.onChange(method?.id)}
                  rhfName="billingInfo.stripeMethodId"
                  label={t('directSignup.eval.payment.labels.creditCard')}
                  disabled={isSubmitting}
                />
              </Elements>
            )}
          </>

          <Text variant="hint" className="mt-8 mb-8">
            {t('directSignup.eligibility.estimates.creditCardDisclaimer')}
          </Text>
        </>
      )}

      {insuranceData.networkStatus === 'Out-of-network' && (
        <>
          <DepositDueCardBody
            price={formatCurrencyDollars(evaluationPrice)}
            insuranceData={insuranceEstimates as InsuranceDefinitionV2}
            isOutOfNetwork
          />
          <div className="flex flex-col gap-y-e16 gap-x-e24 md:flex-row md:justify-between">
            {stripeElement && (
              <Elements stripe={stripeElement}>
                <AppointmentPaymentInput
                  setPaymentMethod={method => field.onChange(method?.id)}
                  rhfName="billingInfo.stripeMethodId"
                  label={t('directSignup.eval.payment.labels.creditCard')}
                  disabled={isSubmitting}
                />
              </Elements>
            )}
          </div>
        </>
      )}
    </>
  );
};

export const InsuranceEligibilityCheckCard: FC<Props> = ({
  result,
  onSubmitEligibilityCheck,
  setShowUpdateInfo,
  isSkippedEligibility,
  onSubmitInsuranceCard,
}) => {
  const { t } = useTranslation();
  const { filters, isResumedLead } = useSignupDirectCtx();
  const { setValue, clearErrors } = useFormContext<EvaluationFormType>();

  if (!filters) throw 'Expected filters to be set';
  const paymentMethod = useWatch<EvaluationFormType, 'billingInfo'>({ name: 'billingInfo' });

  if (!paymentMethodIsInsurance(paymentMethod)) throw 'Expected paymentMethod to be insurance';
  const { insuranceData } = paymentMethod;

  const { track } = useTrack();

  const handleTryAgain = () => {
    if (result === EligibilityCheckResult.ERROR) {
      clearErrors();
      onSubmitEligibilityCheck?.();
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setValue('billingInfo.eligibilityCheckResult', undefined);
      setShowUpdateInfo?.(true);
    }
  };

  if (result == EligibilityCheckResult.OUT_OF_NETWORK || insuranceData.networkStatus === 'Out-of-network') {
    return (
      <BaseCard
        body={<Estimates insuranceData={paymentMethod.insuranceData} />}
        title={t('directSignup.eligibility.outNetwork.title')}
        variant="rejected"
      />
    );
  }

  if (result === EligibilityCheckResult.IN_NETWORK) {
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.INSURANCE_CHECKS.OK, { isResumedLead });
    if (paymentMethod.insuranceData.insuranceType === 'Commercial') {
      return (
        <BaseCard
          body={<Estimates insuranceData={paymentMethod.insuranceData} />}
          title={t('directSignup.eligibility.inNetwork.title')}
          variant="accepted"
          isSkippedEligibility={isSkippedEligibility}
        />
      );
    } else if (paymentMethod.insuranceData.insuranceType === 'Medicare') {
      return (
        <BaseCard
          body={<Estimates insuranceData={paymentMethod.insuranceData} />}
          title={t('directSignup.eligibility.inNetwork.title')}
          variant="accepted"
          isSkippedEligibility={isSkippedEligibility}
        />
      );
    } else {
      if (paymentMethod.insuranceData.insuranceType === 'Medicaid') {
        return (
          <Card noDefaultPaddings>
            <div className="flex flex-col">
              <Heading level="h6" className="gap-e16 flex items-center p-e32" noDefaultMargins>
                <div className="bg-green-100 text-green-600">
                  <SealCheck weight="fill" size={20} />
                </div>
                <div className="w-[1px] bg-warm-gray-200"></div>

                {t('directSignup.eligibility.inNetwork.title')}
              </Heading>

              <div className="h-[1px] bg-warm-gray-200"></div>

              <div className="p-e32">
                <Text variant="label" className="">
                  {t('directSignup.sections.insurance.labels.depositDue')}
                </Text>
                <Text variant="display" className="pt-8">
                  {formatCurrencyDollars(0)}
                </Text>
              </div>
            </div>
          </Card>
        );
      }
      return (
        <BaseCard
          title={t('directSignup.eligibility.inNetwork.title')}
          variant="accepted"
          body={<Estimates insuranceData={paymentMethod.insuranceData} />}
          footer={
            <>
              {(paymentMethod.insuranceData.insuranceType === 'Medicaid' ||
                paymentMethod.insuranceData.insuranceType === 'Medicare') && (
                <div className=" w-full">
                  <Text variant="label" className="mb-e8 block">
                    {t('directSignup.sections.insurance.labels.depositDue')}
                  </Text>
                  <Text variant="display" className="mb-e8 block">
                    {formatCurrencyDollars(0)}
                  </Text>
                </div>
              )}
            </>
          }
        />
      );
    }
  }

  if (result === EligibilityCheckResult.INACTIVE) {
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.INSURANCE_CHECKS.INACTIVE, { isResumedLead });
    return (
      <BaseCard
        title={t('directSignup.eligibility.inactive.title')}
        variant="rejected"
        body={
          <div>
            <Heading level="h4" noDefaultMargins className="mb-e4">
              {t(`directSignup.eligibility.inactive.body.1`)}
            </Heading>
            <Paragraph size="lg" noDefaultMargins>
              {t(`directSignup.eligibility.inactive.body.2`)}
            </Paragraph>
          </div>
        }
      >
        {onSubmitEligibilityCheck && (
          <Button variant="primary" onClick={onSubmitInsuranceCard}>
            {t('directSignup.eligibility.submitInsuranceCard')}
          </Button>
        )}
      </BaseCard>
    );
  }

  if (
    result === EligibilityCheckResult.ERROR ||
    result === EligibilityCheckResult.INCORRECT_MEMBER_ID ||
    result === EligibilityCheckResult.INCORRECT_NAME ||
    result === EligibilityCheckResult.INCORRECT_DOB ||
    result === EligibilityCheckResult.INVALID_PARTICIPANT_ID
  ) {
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.INSURANCE_CHECKS.ERROR, { isResumedLead });
    return (
      <BaseCard
        title={t(`directSignup.eligibility.error.title.${result}`)}
        variant="rejected"
        body={
          <Paragraph size="lg" noDefaultMargins>
            <Text variant="h4" className="mb-e4">
              {t(`directSignup.eligibility.error.body.heading`)}
            </Text>
            {t(`directSignup.eligibility.error.body.${result}`)}
          </Paragraph>
        }
        footer={
          <Paragraph size="sm" noDefaultMargins>
            <Trans
              i18nKey="directSignup.eligibility.error.footer"
              components={[
                <Button
                  variant="link"
                  size="sm"
                  className="text-[#4A5CC5] text-[13px] font-normal"
                  onClick={handleTryAgain}
                />,
              ]}
            />
          </Paragraph>
        }
      >
        {onSubmitEligibilityCheck && (
          <Button variant="primary" onClick={onSubmitInsuranceCard}>
            {t('directSignup.eligibility.submitInsuranceCard')}
          </Button>
        )}
      </BaseCard>
    );
  }
};

const checkInsuranceHasEstimates = (data: InsuranceDefinitionV2) => {
  const {
    expectedEvaluationRate,
    expectedSessionRate,
    averageEvalPatientResponsibility,
    averageSessionPatientResponsibility,
  } = data;

  return ![
    expectedEvaluationRate,
    expectedSessionRate,
    averageEvalPatientResponsibility,
    averageSessionPatientResponsibility,
  ].some(isUndefined);
};
